// Internal imports
import { buttonLink } from '@insify/css/classes.module.css';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { IconicCheck2 } from '../../iconic-icons';
import { headingXxxs, textSm } from '../../typography';
import { AnimatedHeight } from '../AnimatedHeight';
import { Toggle } from '../Toggle';

import * as styles from './SummaryDetails.module.css';

import type { TrackingProps } from '../types';
import type { UseFormRegisterReturn } from 'react-hook-form';

type SummaryProps = TrackingProps & {
  className?: string;
  icon?: React.ReactNode;
  summary: React.ReactNode;
  details?: React.ReactNode;
  detailsRef?: React.MutableRefObject<HTMLElement | null>;
  onDetailsToggle?: (isOpen: boolean) => void;
};

export const SummaryDetails: React.FC<SummaryProps> = ({
  trackingId,
  className,
  icon,
  summary,
  details,
  detailsRef = { current: null },
  onDetailsToggle,
}) => {
  const [isOpened, setIsOpened] = useState(false);

  const toggleDetails = (): void => {
    const newState = !isOpened;
    setIsOpened(newState);
    onDetailsToggle?.(newState);
  };

  useEffect(() => {
    const currentRef = detailsRef.current;
    if (currentRef) {
      currentRef.addEventListener('click', toggleDetails);
      return () => {
        currentRef.removeEventListener('click', toggleDetails);
      };
    }
    return undefined;
  }, [detailsRef, isOpened, toggleDetails]);

  return (
    <div className={clsx(styles.summaryDetailsWrapper, className)}>
      {icon && <div className={styles.iconWrapper}>{icon}</div>}
      <div className={styles.summaryDetailsContentWrapper}>
        <div>{summary}</div>
        {details && (
          <AnimatedHeight trackingId={trackingId} className={styles.details} height={isOpened ? 'auto' : '0px'}>
            {details}
          </AnimatedHeight>
        )}
      </div>
    </div>
  );
};

type SummaryDetailsToggleProps = Omit<SummaryProps, 'summary'> & {
  summaryText: React.ReactNode;
  summaryButtonLinkText?: React.ReactNode;
  summaryPowerUpComponent?: React.ReactNode;
  toggleId: string;
  toggleRegisterReturn: UseFormRegisterReturn;
  isIncluded?: boolean;
  includedText?: string;
};

export const SummaryDetailsToggle: React.FC<SummaryDetailsToggleProps> = ({
  trackingId,
  className,
  icon,
  summaryText,
  summaryButtonLinkText,
  summaryPowerUpComponent,
  details,
  detailsRef = { current: null },
  onDetailsToggle,
  toggleId,
  isIncluded = false,
  includedText = '',
  toggleRegisterReturn,
}) => (
  <SummaryDetails
    trackingId={trackingId}
    className={clsx(className)}
    icon={icon}
    summary={
      <div className={styles.summaryCardWrapper}>
        <div className={styles.summaryTextWrapper}>
          <div className={styles.summaryTextFirstRow}>
            <div className={styles.summaryFirstRowText}>{summaryText}</div>
          </div>
          <div className={styles.summaryTextSecondRow}>
            <div
              className={clsx(styles.summaryTextButtonLink, textSm, buttonLink)}
              ref={detailsRef as React.RefObject<HTMLDivElement>}
            >
              {summaryButtonLinkText}
            </div>
            <div className={styles.summaryInputToggle}>
              {summaryPowerUpComponent}
              {isIncluded ? (
                <div className={clsx(styles.included, headingXxxs)}>
                  <IconicCheck2 />
                  {includedText}
                </div>
              ) : (
                <Toggle
                  id={toggleId}
                  trackingId='toggle'
                  data-testid={`${toggleId}-toggle`}
                  {...toggleRegisterReturn}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    }
    details={details && <div className={styles.detailsContainer}>{details}</div>}
    detailsRef={detailsRef}
    onDetailsToggle={onDetailsToggle}
  />
);
